/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import { UtrvStatus } from '../../constants/status';
import { useAppSelector } from '../../reducers';
import { generateUrl } from '../../routes/util';
import G17Client from '../../services/G17Client';
import {
  SurveyRange,
  CustomReportType,
  NewDateComparisonReport,
  DateReportType,
} from '../../types/custom-report';
import { SubmitButton } from '../button/SubmitButton';
import { FieldProps, FormGenerator } from '../form/FormGenerator';
import Loader from '../loader';
import { CustomReportRoutes } from './CustomReportsRoot';
import { DateSurveySelectionTable } from './partials/DateSurveySelectionTable';
import './styles.scss';
import { convertToUtrvStatuses, DownloadUtrvStatusCombined, FormData, getInitialState } from './util/downloadReportHandler';
import { QUESTION } from '../../constants/terminology';

const fields: FieldProps[] = [
  {
    code: 'name',
    type: 'text',
    label: 'Name of date comparison report',
    placeholder: 'For example: January to December 2022 review',
    required: true,
  },
  {
    code: 'description',
    type: 'textarea',
    label: 'Description',
    placeholder: 'Provide a description here...',
  },
  {
    code: 'status',
    type: 'radio',
    label: 'Data I want to download:',
    placeholder: '',
    options: [
      {
        value: DownloadUtrvStatusCombined.All,
        label: `All ${QUESTION.PLURAL} (including unanswered)`,
      },
      {
        value: DownloadUtrvStatusCombined.AllAnswered,
        label: `All answered ${QUESTION.PLURAL} (answered and verified)`,
      },
      {
        value: UtrvStatus.Verified,
        label: `Verified ${QUESTION.PLURAL} only`,
      },
      {
        value: DownloadUtrvStatusCombined.Assured,
        label: `Assured ${QUESTION.PLURAL} only`
      },
    ]
  },
];

interface CustomReportEditFormDateProps {
  customReport?: SurveyRange;
  handleReload: () => Promise<void>;
}

const isFormValid = (formData?: Partial<FormData>, surveyIds?: string[]) => {
  return formData?.name && surveyIds && surveyIds.length > 0;
};

const CustomReportEditFormDate = (props: CustomReportEditFormDateProps) => {
  const { customReport } = props;
  const history = useHistory();
  const [formData, setFormData] = useState<Partial<FormData>>(getInitialState(customReport));
  const [selectedSurveyIds, setSelectedSurveyIds] = useState<string[]>([]);
  const [surveyType, setSurveyType] = useState<DateReportType>(CustomReportType.Survey);
  const [isSaving, setSaving] = useState(false);

  const initiativeState = useAppSelector((state) => state.initiative);

  React.useEffect(() => {
    setFormData(getInitialState(customReport));
    setSelectedSurveyIds(customReport?.survey?.ids ?? []);
  }, [customReport]);

  if (!initiativeState.loaded) {
    return <Loader />;
  }

  const initiativeId = initiativeState.id;

  const handleUpdate = (e: React.ChangeEvent<{ name: string; value: string | number | undefined }>) => {
    const { name, value } = e.target;

    if (!fields.map((f) => f.code).includes(name)) {
      return;
    }

    setFormData({
      ...(formData ?? {}),
      [name]: value,
    });
  };

  const isEditMode = Boolean(formData?._id);

  const handleCancel = () => {
    const url = generateUrl(CustomReportRoutes.ROOT, { initiativeId });
    history.push(url);
  };

  const handleSubmit = async () => {
    if (!isFormValid(formData, selectedSurveyIds)) {
      return;
    }

    setSaving(true);

    const data: NewDateComparisonReport = {
      type: surveyType,
      name: formData.name ?? '',
      description: formData.description,
      survey: {
        ids: selectedSurveyIds,
        ...convertToUtrvStatuses(formData.status),
      },
    };

    const method = formData._id
      ? G17Client.updateCustomReport(initiativeId, formData._id, data)
      : G17Client.createCustomReport(initiativeId, data);
    method
      .then((data) => {
        setSaving(false);
        setFormData({});
        setSelectedSurveyIds([]);
        const url = generateUrl(CustomReportRoutes.ROOT, { initiativeId });
        history.push(url);
      })
      .catch(() => {
        setSaving(false);
      });
  };

  return (
    <>
      <FormGenerator
        form={{ ...formData }}
        fields={fields.map((f) => ({ ...f, disabled: isSaving }))}
        updateForm={handleUpdate}
      />

      <div className='mt-5'>
        <hr />
        <div className='mt-4 strong'>Please select which surveys you would like to compare:</div>
        <div className='mt-3 downloads-table-container'>
          <DateSurveySelectionTable
            initiativeId={initiativeId}
            defaultSelected={customReport?.survey?.ids}
            defaultType={customReport?.type}
            selectedSurveyIds={selectedSurveyIds}
            handleSelectSurveyType={(type) => setSurveyType(type)}
            handleSelectSurveys={(ids: string[]) => setSelectedSurveyIds(ids)}
          />
        </div>
      </div>
      <div className='mt-4 text-right'>
        <Button color='link-secondary' onClick={handleCancel} className='mr-4'>
          Cancel
        </Button>
        <SubmitButton
          disabled={!isFormValid(formData, selectedSurveyIds) || isSaving}
          color='primary'
          onClick={handleSubmit}
        >
          {isEditMode ? 'Update' : 'Create'}
        </SubmitButton>
      </div>
    </>
  );
};

export default CustomReportEditFormDate;
